import React from 'react';
import { motion } from 'framer-motion';
import { IoCalendarOutline, IoCheckmarkCircle } from 'react-icons/io5';

const KitDurationSelection = ({ kitDuration, setKitDuration }) => {
  const durationOptions = [
    {
      value: '12-day',
      label: '12 Day Pack',
      subtitle: '3x per week',
      description: 'Dressing changes 3 times per week',
      color: 'blue'
    },
    {
      value: '30-day',
      label: '30 Day Pack',
      subtitle: 'Daily',
      description: 'Daily dressing changes',
      color: 'purple'
    }
  ];

  return (
    <motion.div 
      className="mb-8 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-2 border-indigo-300 dark:border-indigo-700 rounded-2xl p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-black">
          <IoCalendarOutline className="text-xl" />
        </div>
        <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase">
          Pack Duration
        </h3>
        <span className="text-red-500 text-xl">*</span>
      </div>

      <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-medium">
        Do you require a 12 day pack (3x/week) or a 30 day pack (daily dressing change)?
      </p>

      {/* Duration Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {durationOptions.map((option) => (
          <motion.button
            key={option.value}
            onClick={() => setKitDuration(option.value)}
            className={`relative p-4 rounded-xl border-2 text-left transition-all duration-300 ${
              kitDuration === option.value
                ? `border-${option.color}-500 bg-gradient-to-br from-${option.color}-50 to-${option.color}-100 dark:from-${option.color}-900/30 dark:to-${option.color}-800/30 shadow-lg`
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-indigo-400 dark:hover:border-indigo-500'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Selection Checkmark */}
            {kitDuration === option.value && (
              <motion.div
                className="absolute top-3 right-3"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <IoCheckmarkCircle className="text-2xl text-indigo-600 dark:text-indigo-400" />
              </motion.div>
            )}

            {/* Content */}
            <div className="pr-8">
              <h4 className="text-lg font-black text-gray-900 dark:text-white mb-1">
                {option.label}
              </h4>
              <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400 mb-2">
                {option.subtitle}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {option.description}
              </p>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Helper Text */}
      {!kitDuration && (
        <motion.p
          className="text-xs text-amber-600 dark:text-amber-400 mt-3 font-medium flex items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <span className="text-lg">⚠️</span>
          Please select a pack duration to continue
        </motion.p>
      )}

      {/* Selected Confirmation */}
      {kitDuration && (
        <motion.div
          className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-2"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <IoCheckmarkCircle className="text-xl text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-800 dark:text-green-300 font-medium">
            Selected: <span className="font-bold">
              {durationOptions.find(opt => opt.value === kitDuration)?.label}
            </span> - {durationOptions.find(opt => opt.value === kitDuration)?.description}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default KitDurationSelection;